import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { WeeklyTimesheet, WeeklyTimesheetEntry } from '../types/timesheet.ts';
import { Employee } from '../types/employee.ts';
import { Contractor } from '../types/contractor.ts';
import { calculateBurdenedRate, calculateContractorRates } from '../utils/rateCalculations.ts';
import { FileText, ArrowRight, CheckCircle, AlertCircle, User, Calendar, Clock } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoiceNumberingService } from '../services/invoiceNumberingService.ts';

interface ProcessedTimesheetData {
  'INV #': string;
  'EMPLOYEE': string;
  'PAY TYPE': string;
  'Activity Code': string;
  'Activity Description': string;
  'JOB NAME': string;
  'JOB NUMBER': string;
  'WEEK ENDING': string;
  'HOURS': number;
  'BURDENED RATE': number;
  'CONTRACTOR_ID'?: string;
  'CONTRACTOR_NAME'?: string;
}

interface ContractorGroup {
  contractorPrefix: string;
  contractorName: string;
  contractorId?: string;
  invoiceNumber: string;
  employees: {
    [employeeName: string]: {
      regularHours: number;
      overtimeHours: number;
      regularRate: number;
      overtimeRate: number;
      activityCode?: string;
      activityDescription?: string;
    }
  };
}

const TimesheetToInvoiceConverter: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [timesheet, setTimesheet] = useState<WeeklyTimesheet | null>(null);
  const [employees, setEmployees] = useState<{ [id: string]: Employee }>({});
  const [contractors, setContractors] = useState<{ [id: string]: Contractor }>({});
  const [contractorGroups, setContractorGroups] = useState<ContractorGroup[]>([]);
  const [nextInvoiceNumbers, setNextInvoiceNumbers] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (id) {
      loadTimesheetAndEmployees();
      loadContractors();
    }
  }, [id]);

  useEffect(() => {
    if (timesheet && Object.keys(employees).length > 0) {
      processTimesheetForInvoicing();
    }
  }, [timesheet, employees, contractors]);

  useEffect(() => {
    // Load next invoice numbers for display
    const loadNextInvoiceNumbers = async () => {
      try {
        const numbers = await invoiceNumberingService.getNextInvoiceNumbers();
        setNextInvoiceNumbers(numbers);
      } catch (error) {
        console.error('Error loading next invoice numbers:', error);
      }
    };
    
    loadNextInvoiceNumbers();
  }, []);

  const loadTimesheetAndEmployees = async () => {
    try {
      setLoading(true);
      
      // Load timesheet
      const timesheetRef = doc(db, 'weeklyTimesheets', id!);
      const timesheetSnap = await getDoc(timesheetRef);
      
      if (!timesheetSnap.exists()) {
        setError('Timesheet not found');
        return;
      }
      
      const timesheetData = {
        id: timesheetSnap.id,
        ...timesheetSnap.data(),
        weekEnding: timesheetSnap.data()?.weekEnding?.toDate() || new Date(),
        createdAt: timesheetSnap.data()?.createdAt?.toDate() || new Date(),
        updatedAt: timesheetSnap.data()?.updatedAt?.toDate() || new Date()
      } as WeeklyTimesheet;
      
      setTimesheet(timesheetData);
      
      // Load employee data for all employees in the timesheet
      const employeeIds = timesheetData.entries
        .map(entry => entry.employeeId)
        .filter(id => id); // Filter out empty IDs
      
      if (employeeIds.length > 0) {
        const employeeData: { [id: string]: Employee } = {};
        
        // Load each employee individually
        for (const empId of employeeIds) {
          try {
            const empRef = doc(db, 'employees', empId);
            const empSnap = await getDoc(empRef);
            
            if (empSnap.exists()) {
              employeeData[empId] = {
                id: empSnap.id,
                ...empSnap.data(),
                createdAt: empSnap.data()?.createdAt?.toDate() || new Date(),
                updatedAt: empSnap.data()?.updatedAt?.toDate() || new Date(),
                hireDate: empSnap.data()?.hireDate?.toDate(),
                terminationDate: empSnap.data()?.terminationDate?.toDate(),
                licenseExpiration: empSnap.data()?.licenseExpiration?.toDate(),
              } as Employee;
            }
          } catch (err) {
            console.error(`Error loading employee ${empId}:`, err);
          }
        }
        
        setEmployees(employeeData);
      }
    } catch (err) {
      console.error('Error loading timesheet:', err);
      setError('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  };

  const loadContractors = async () => {
    try {
      const contractorsRef = collection(db, 'contractors');
      const q = query(contractorsRef, orderBy('companyName'));
      const snapshot = await getDocs(q);
      
      const contractorData: { [id: string]: Contractor } = {};
      snapshot.docs.forEach(doc => {
        contractorData[doc.id] = {
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          licenseExpires: doc.data().licenseExpires?.toDate(),
          insuranceExpires: doc.data().insuranceExpires?.toDate(),
          bondExpires: doc.data().bondExpires?.toDate(),
          lastLoginAt: doc.data().lastLoginAt?.toDate()
        } as Contractor;
      });
      
      setContractors(contractorData);
    } catch (err) {
      console.error('Error loading contractors:', err);
      // Don't set error as this is not critical
    }
  };

  const processTimesheetForInvoicing = async () => {
    if (!timesheet) return;

    console.log('Processing timesheet for invoicing preview:', timesheet.jobName);
    console.log('Available employees:', Object.keys(employees));
    console.log('Timesheet entries:', timesheet.entries.length);
    console.log('Timesheet contractor info:', { contractorId: timesheet.contractorId, contractorName: timesheet.contractorName });

    // Group employees by contractor based on their rates and determine contractor
    const groups: { [key: string]: ContractorGroup } = {};

    // First pass: collect unique contractor prefixes and peek at next invoice numbers (don't reserve yet)
    const contractorPrefixes = new Set<string>();
    
    // Check if timesheet has a pre-assigned contractor
    let timesheetContractorId: string | null = null;
    let timesheetContractor: Contractor | null = null;
    
    if (timesheet.contractorId && contractors[timesheet.contractorId]) {
      timesheetContractorId = timesheet.contractorId;
      timesheetContractor = contractors[timesheet.contractorId];
      console.log('Timesheet has pre-assigned contractor:', timesheetContractor.companyName);
    }
    
    timesheet.entries.forEach(entry => {
      console.log('Processing entry for employee:', entry.employeeName, 'ID:', entry.employeeId);
      
      if (!entry.employeeId || !employees[entry.employeeId]) {
        console.warn('Employee not found in database:', entry.employeeName, entry.employeeId);
        setError(`Employee "${entry.employeeName}" not found in database. Please ensure all employees are properly registered.`);
        return;
      }

      const employee = employees[entry.employeeId];
      const totalHours = Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
      
      console.log(`Employee ${employee.firstName} ${employee.lastName} rates:`, employee.rates);
      console.log(`Total hours: ${totalHours}`);
      
      if (totalHours === 0) return; // Skip entries with no hours

      // Determine contractor prefix - if timesheet has a contractor assigned, use it for ALL employees
      let contractorPrefix = '';
      
      if (timesheetContractor) {
        // Use the contractor's invoicePrefix if it has one, otherwise fallback to legacy logic
        if (timesheetContractor.invoicePrefix) {
          contractorPrefix = timesheetContractor.invoicePrefix;
          console.log(`Using contractor's invoicePrefix "${contractorPrefix}" for employee ${employee.firstName} ${employee.lastName}`);
        } else {
          // Legacy fallback based on company name for contractors without invoicePrefix set
          const contractorName = timesheetContractor.companyName.toLowerCase();
          if (contractorName.includes('btc') || contractorName.includes('build.test.connect') || contractorName.includes('build test connect')) {
            contractorPrefix = 'BTC';
          } else if (contractorName.includes('reliable') || contractorName.includes('rel')) {
            contractorPrefix = 'REL';
          } else if (contractorName.includes('cec') || contractorName.includes('commonwealth')) {
            contractorPrefix = ''; // CEC uses no prefix
          } else {
            contractorPrefix = ''; // Default to CEC (no prefix)
          }
          console.log(`Using timesheet contractor ${timesheetContractor.companyName} -> ${contractorPrefix || 'CEC (no prefix)'} for employee ${employee.firstName} ${employee.lastName} (legacy fallback)`);
        }
      } else {
        // Fallback: determine by employee rates and find matching contractor
        const contractorEntries = Object.entries(contractors);
        let matchedContractor: Contractor | null = null;
        
        if (employee.rates?.btc && employee.rates.btc > 0) {
          matchedContractor = contractorEntries.find(([_, c]) => c.companyName.toLowerCase().includes('btc') || c.companyName.toLowerCase().includes('build'))?.[1] || null;
          console.log(`Employee ${employee.firstName} ${employee.lastName} assigned to BTC by rate (rate: ${employee.rates.btc})`);
        } else if (employee.rates?.reliable && employee.rates.reliable > 0) {
          matchedContractor = contractorEntries.find(([_, c]) => c.companyName.toLowerCase().includes('reliable'))?.[1] || null;
          console.log(`Employee ${employee.firstName} ${employee.lastName} assigned to REL by rate (rate: ${employee.rates.reliable})`);
        } else {
          matchedContractor = contractorEntries.find(([_, c]) => c.companyName.toLowerCase().includes('cec') || c.companyName.toLowerCase().includes('commonwealth'))?.[1] || null;
          console.log(`Employee ${employee.firstName} ${employee.lastName} assigned to CEC by rate (rate: ${employee.rates?.cec || employee.hourlyWage})`);
        }
        
        // Use the matched contractor's invoicePrefix if available
        if (matchedContractor && matchedContractor.invoicePrefix) {
          contractorPrefix = matchedContractor.invoicePrefix;
        } else {
          // Legacy prefix determination
          if (employee.rates?.btc && employee.rates.btc > 0) {
            contractorPrefix = 'BTC';
          } else if (employee.rates?.reliable && employee.rates.reliable > 0) {
            contractorPrefix = 'REL';
          } else {
            contractorPrefix = ''; // CEC uses no prefix
          }
        }
      }

      contractorPrefixes.add(contractorPrefix);
    });

    // Peek at next invoice numbers for preview (don't reserve them yet)
    console.log('Contractor prefixes found:', Array.from(contractorPrefixes));
    const prefixInvoiceMap: { [prefix: string]: string } = {};
    for (const prefix of contractorPrefixes) {
      try {
        console.log(`Peeking at next invoice number for ${prefix || 'CEC (no prefix)'}...`);
        // Use 'CEC' as the service key for empty prefix
        const servicePrefix = prefix || 'CEC';
        const nextInvoiceNumber = await invoiceNumberingService.peekNextInvoiceNumber(servicePrefix);
        // Extract just the numeric part for internal use
        const numericPart = servicePrefix === 'CEC' ? nextInvoiceNumber : nextInvoiceNumber.split('-')[1];
        prefixInvoiceMap[prefix] = numericPart;
        console.log(`Next invoice number for ${prefix || 'no prefix'}: ${nextInvoiceNumber} (using ${numericPart} internally)`);
      } catch (error) {
        console.error(`Error getting next invoice number for ${prefix || 'no prefix'}:`, error);
        // Fallback to sequential numbering
        const fallbackNumber = (Object.keys(prefixInvoiceMap).length + 1001).toString();
        prefixInvoiceMap[prefix] = fallbackNumber;
        console.log(`Using fallback invoice number for ${prefix || 'no prefix'}: ${fallbackNumber}`);
      }
    }

    // Second pass: create contractor groups with reserved invoice numbers
    timesheet.entries.forEach(entry => {
      if (!entry.employeeId || !employees[entry.employeeId]) {
        return; // Skip employees not found in database
      }

      const employee = employees[entry.employeeId];
      const totalHours = Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
      
      if (totalHours === 0) return; // Skip entries with no hours

      // Determine contractor prefix and calculate enhanced burdened rates
      let contractorPrefix = '';
      let contractorName = 'CEC Facilities Services';
      let regularRate = employee.rates?.cec || employee.hourlyWage || 0;
      let contractorForRates: Contractor | null = null;

      // Use timesheet contractor if assigned, otherwise determine from employee rates
      if (timesheetContractor) {
        contractorForRates = timesheetContractor;
        contractorName = timesheetContractor.companyName;
        
        // Use contractor's invoicePrefix if available, otherwise legacy logic
        if (timesheetContractor.invoicePrefix) {
          contractorPrefix = timesheetContractor.invoicePrefix;
        } else {
          // Legacy prefix determination from company name
          const contractorNameLower = timesheetContractor.companyName.toLowerCase();
          if (contractorNameLower.includes('btc') || contractorNameLower.includes('build.test.connect') || contractorNameLower.includes('build test connect')) {
            contractorPrefix = 'BTC';
          } else if (contractorNameLower.includes('reliable') || contractorNameLower.includes('rel')) {
            contractorPrefix = 'REL';
          } else if (contractorNameLower.includes('cec') || contractorNameLower.includes('commonwealth')) {
            contractorPrefix = ''; // CEC uses no prefix
          } else {
            contractorPrefix = ''; // Default to CEC (no prefix)
          }
        }
        
        // Determine which employee rate to use based on contractor
        if (contractorPrefix === 'BTC') {
          regularRate = employee.rates?.btc || employee.hourlyWage || 0;
        } else if (contractorPrefix === 'REL') {
          regularRate = employee.rates?.reliable || employee.hourlyWage || 0;
        } else {
          regularRate = employee.rates?.cec || employee.hourlyWage || 0;
        }
        
        console.log(`Using timesheet contractor ${contractorName} (${contractorPrefix || 'no prefix'}) for rate calculation`);
      } else {
        // Fallback: determine contractor based on employee rates
        const contractorEntries = Object.entries(contractors);
        
        if (employee.rates?.btc && employee.rates.btc > 0) {
          contractorName = 'Build.Test.Connect';
          regularRate = employee.rates.btc;
          contractorForRates = contractorEntries.find(([_, c]) => c.companyName.toLowerCase().includes('btc') || c.companyName.toLowerCase().includes('build'))?.[1] || null;
          contractorPrefix = contractorForRates?.invoicePrefix || 'BTC'; // Use contractor's prefix or legacy default
        } else if (employee.rates?.reliable && employee.rates.reliable > 0) {
          contractorName = 'Reliable Contractors';
          regularRate = employee.rates.reliable;
          contractorForRates = contractorEntries.find(([_, c]) => c.companyName.toLowerCase().includes('reliable'))?.[1] || null;
          contractorPrefix = contractorForRates?.invoicePrefix || 'REL'; // Use contractor's prefix or legacy default
        } else {
          // Default to CEC
          contractorForRates = contractorEntries.find(([_, c]) => c.companyName.toLowerCase().includes('cec') || c.companyName.toLowerCase().includes('commonwealth'))?.[1] || null;
          contractorPrefix = contractorForRates?.invoicePrefix || ''; // Use contractor's prefix or no prefix for CEC
        }
        
        console.log(`Determined contractor from employee rates: ${contractorName} (${contractorPrefix || 'no prefix'})`);
      }

      // Use enhanced rate calculation if contractor configuration is available
      if (contractorForRates) {
        const contractorConfig = {
          markupType: contractorForRates.billing.markupType,
          markupValue: contractorForRates.billing.markupValue
        };

        // Prepare rate inputs for calculation
        const rateInputs = {
          hourlyWage: employee.hourlyWage || 0,
          insurance: employee.insurance || 0,
          vacation: employee.vacation || 0,
          carFuel: employee.carFuel || 0,
          markup: employee.markup || 0,
          customRateAdjustments: employee.customRateAdjustments
        };

        // Calculate enhanced burdened rate
        regularRate = calculateBurdenedRate(rateInputs, contractorConfig);
        
        console.log(`Enhanced rate calculation for ${employee.firstName} ${employee.lastName} with ${contractorForRates.companyName}: $${regularRate.toFixed(2)} (markup: ${contractorConfig.markupType} ${contractorConfig.markupValue})`);
      } else {
        console.log(`No contractor configuration found for ${contractorPrefix}, using base rate: $${regularRate.toFixed(2)}`);
      }

      if (!groups[contractorPrefix]) {
        groups[contractorPrefix] = {
          contractorPrefix,
          contractorName,
          contractorId: contractorForRates?.id || timesheetContractor?.id,
          invoiceNumber: prefixInvoiceMap[contractorPrefix],
          employees: {}
        };
      }

      // Calculate regular and overtime hours (over 40 hours = overtime)
      const regularHours = Math.min(totalHours, 40);
      const overtimeHours = Math.max(totalHours - 40, 0);

      groups[contractorPrefix].employees[entry.employeeName] = {
        regularHours,
        overtimeHours,
        regularRate,
        overtimeRate: regularRate * 1.5,
        activityCode: entry.activityCode,
        activityDescription: entry.activityDescription
      };
    });

    setContractorGroups(Object.values(groups));
  };

  const convertToInvoiceFormat = (): ProcessedTimesheetData[] => {
    if (!timesheet || contractorGroups.length === 0) return [];

    const invoiceData: ProcessedTimesheetData[] = [];
    const weekEndingStr = timesheet.weekEnding.toISOString().split('T')[0];

    contractorGroups.forEach(group => {
      Object.entries(group.employees).forEach(([employeeName, empData]) => {
        // Add regular hours entry
        if (empData.regularHours > 0) {
          // Format invoice number with prefix if the contractor has one
          const formattedInvoiceNumber = group.contractorPrefix 
            ? `${group.contractorPrefix}-${group.invoiceNumber}`
            : group.invoiceNumber;
            
          invoiceData.push({
            'INV #': formattedInvoiceNumber,
            'EMPLOYEE': employeeName,
            'PAY TYPE': 'Regular',
            'Activity Code': empData.activityCode || '',
            'Activity Description': empData.activityDescription || '',
            'JOB NAME': timesheet.jobName,
            'JOB NUMBER': timesheet.jobNumber || '',
            'WEEK ENDING': weekEndingStr,
            'HOURS': empData.regularHours,
            'BURDENED RATE': empData.regularRate,
            'CONTRACTOR_ID': group.contractorId || '',
            'CONTRACTOR_NAME': group.contractorName || ''
          });
        }

        // Add overtime hours entry
        if (empData.overtimeHours > 0) {
          // Format invoice number with prefix if the contractor has one
          const formattedInvoiceNumber = group.contractorPrefix 
            ? `${group.contractorPrefix}-${group.invoiceNumber}`
            : group.invoiceNumber;
            
          invoiceData.push({
            'INV #': formattedInvoiceNumber,
            'EMPLOYEE': employeeName,
            'PAY TYPE': 'Overtime',
            'Activity Code': empData.activityCode || '',
            'Activity Description': empData.activityDescription || '',
            'JOB NAME': timesheet.jobName,
            'JOB NUMBER': timesheet.jobNumber || '',
            'WEEK ENDING': weekEndingStr,
            'HOURS': empData.overtimeHours,
            'BURDENED RATE': empData.overtimeRate,
            'CONTRACTOR_ID': group.contractorId || '',
            'CONTRACTOR_NAME': group.contractorName || ''
          });
        }
      });
    });

    return invoiceData;
  };

  const handleConvertToInvoice = async () => {
    if (!timesheet || !currentUser) return;

    try {
      setConverting(true);
      
      // Just convert to invoice format using preview numbers (don't reserve yet)
      console.log('Converting timesheet to invoice format...');
      const invoiceData = convertToInvoiceFormat();
      
      if (invoiceData.length === 0) {
        setError('No valid invoice data to convert');
        return;
      }

      // Store the processed data in localStorage for the ExcelInvoiceGenerator
      localStorage.setItem('processedTimesheetData', JSON.stringify(invoiceData));
      
      // Update timesheet status
      await updateDoc(doc(db, 'weeklyTimesheets', timesheet.id), {
        status: 'processed_for_invoice',
        processedForInvoice: true,
        invoiceNumbers: contractorGroups.map(g => g.invoiceNumber),
        updatedAt: new Date()
      });

      setSuccess('Timesheet converted to invoice format successfully!');
      
      // Navigate to invoice generator after a short delay
      setTimeout(() => {
        navigate('/invoices/excel-generator');
      }, 2000);

    } catch (err) {
      console.error('Error converting to invoice:', err);
      setError('Failed to convert timesheet to invoice format');
    } finally {
      setConverting(false);
    }
  };

  const getTotalHours = (entry: WeeklyTimesheetEntry): number => {
    return Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!timesheet) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Timesheet not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The timesheet you're trying to convert could not be found.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/timesheets')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Back to Timesheets
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Convert to Invoice</h1>
            <p className="text-gray-600 mt-1">
              Convert timesheet data to invoice format for processing
            </p>
          </div>
          <button
            onClick={() => navigate('/timesheets')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Back to Timesheets
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      {/* Timesheet Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Timesheet Summary
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Job Name</label>
            <p className="text-lg font-semibold text-gray-900">{timesheet.jobName}</p>
          </div>
          {timesheet.jobNumber && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Job Number</label>
              <p className="text-lg font-semibold text-gray-900">{timesheet.jobNumber}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Week Ending</label>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1 text-gray-400" />
              <p className="text-lg font-semibold text-gray-900">
                {timesheet.weekEnding.toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4 bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Next Invoice Numbers</h3>
              <p className="text-sm text-blue-700 mt-1">
                Invoice numbers are automatically assigned based on employee contractor assignments.
              </p>
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                {Object.entries(nextInvoiceNumbers).map(([prefix, number]) => (
                  <div key={prefix} className="bg-white rounded px-2 py-1">
                    <span className="font-medium text-gray-600">{prefix}:</span> 
                    <span className="ml-1 text-gray-900">{number || 'Loading...'}</span>
                  </div>
                ))}
                {Object.keys(nextInvoiceNumbers).length === 0 && (
                  <div className="col-span-3 bg-white rounded px-2 py-1 text-center text-gray-500">
                    Loading invoice numbers...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Hours Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Employee Hours Summary
        </h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overtime</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {timesheet.entries.map((entry, index) => {
                const totalHours = getTotalHours(entry);
                const employee = entry.employeeId ? employees[entry.employeeId] : null;
                const regularHours = Math.min(totalHours, 40);
                const overtimeHours = Math.max(totalHours - 40, 0);
                
                return (
                  <tr key={index} className={!employee ? 'bg-yellow-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{entry.employeeName}</div>
                      {!employee && (
                        <div className="text-xs text-yellow-600">⚠️ Not found in database</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{totalHours.toFixed(1)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {regularHours.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {overtimeHours > 0 ? (
                        <span className="text-orange-600 font-medium">{overtimeHours.toFixed(1)}</span>
                      ) : (
                        '0'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {employee ? (
                        <div>
                          <div>CEC: ${employee.rates?.cec || 'N/A'}</div>
                          {employee.rates?.reliable && <div>REL: ${employee.rates.reliable}</div>}
                          {employee.rates?.btc && <div>BTC: ${employee.rates.btc}</div>}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                      {entry.activityCode && (
                        <div>
                          <div className="font-medium">{entry.activityCode}</div>
                          <div>{entry.activityDescription}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {employee ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Ready
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Missing Data
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Contractor Groups Preview */}
      {contractorGroups.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Invoice Preview ({contractorGroups.length} invoice{contractorGroups.length > 1 ? 's' : ''})
          </h2>
          
          <div className="space-y-4">
            {contractorGroups.map((group, index) => (
              <div key={index} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium text-gray-900">
                    Invoice #{group.contractorPrefix ? `${group.contractorPrefix}-${group.invoiceNumber}` : group.invoiceNumber} - {group.contractorName}
                  </h3>
                  <span className="text-sm text-gray-500">
                    {Object.keys(group.employees).length} employee{Object.keys(group.employees).length > 1 ? 's' : ''}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                  {Object.entries(group.employees).map(([empName, empData]) => (
                    <div key={empName} className="bg-white p-2 rounded border">
                      <div className="font-medium">{empName}</div>
                      <div className="text-gray-600">
                        Regular: {empData.regularHours}h @ ${empData.regularRate}
                      </div>
                      {empData.overtimeHours > 0 && (
                        <div className="text-orange-600">
                          Overtime: {empData.overtimeHours}h @ ${empData.overtimeRate}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Convert Action */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Ready to Convert</h3>
            <p className="text-gray-600">
              This will convert the timesheet to invoice format and redirect you to the invoice generator.
            </p>
          </div>
          <button
            onClick={handleConvertToInvoice}
            disabled={converting || contractorGroups.length === 0}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {converting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Converting...
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4 mr-2" />
                Convert to Invoice
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimesheetToInvoiceConverter;